<template>
  <div style="position: relative;" class="sentiment-chart">
    <h3 v-if="title">{{ title }}</h3>
    <v-icon
      v-if="showDescriptionIcon && !ifLoading"
      small
      color="blue"
      v-on:click.stop.prevent="showPopup = !showPopup"
      title="Sentiment description"
      class="description-icon"
      >fas fa-info-circle</v-icon
    >
    <SentimentInfoDialog
      :open="showPopup"
      :dark="darkDialog"
    ></SentimentInfoDialog>

    <v-skeleton-loader
      v-if="ifLoading"
      type="image"
      alt="Getting stats..."
    ></v-skeleton-loader>

    <div
      class="icon-container"
      :class="{
        'no-title': !title,
      }"
    >
      <v-icon
        v-if="chartOptions && includeRefreshButton && !ifLoading"
        color="#aaa"
        size="14"
        class="reload-icon"
        title="Reload Chart"
        @click="reloadChart"
        >fas fa-redo</v-icon
      >
    </div>
    <div v-if="chartOptions && chartOptions.xAxis && !ifLoading && !isEmpty">
      <v-chart
        v-if="chartOptions"
        ref="chartInstance"
        :option="chartOptions"
        style="width: 100%; height: 20vh;"
        @finished="chartFinished"
      />
    </div>
    <p v-if="isEmpty && !ifLoading" class="message">
      There are no sentiments to show.
    </p>
  </div>
</template>

<script>
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import {
  GridComponent,
  ToolboxComponent,
  TooltipComponent,
  VisualMapComponent,
  MarkLineComponent,
  MarkPointComponent,
} from "echarts/components";

import { BarChart } from "echarts/charts";

use([
  CanvasRenderer,
  GridComponent,
  ToolboxComponent,
  VisualMapComponent,
  TooltipComponent,
  MarkLineComponent,
  MarkPointComponent,
  BarChart,
]);

const EMITTED_ACTIONS = {
  FINISHED: "finished",
};

export default {
  name: "SentimentChart",
  components: {
    SentimentInfoDialog: () =>
      import("@/components/common/dialogs/SentimentInfoDialog"),
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    histogram: {
      type: Array,
      required: true,
    },
    markLineInterval: {
      type: Number,
      default: 30,
    },
    positiveBarColor: {
      type: String,
      default: "#7ED321",
    },
    negativeBarColor: {
      type: String,
      default: "#FF496F",
    },
    markLineColor: {
      type: String,
      default: "#ffffff",
    },
    dateFormat: {
      type: String,
      default: "YYYY-MM-DD",
    },
    refresh: {
      type: String,
    },
    includeRefreshButton: {
      type: Boolean,
      default: false,
    },
    showDescriptionIcon: {
      type: Boolean,
      default: true,
    },
    darkDialog: {
      type: Boolean,
      default: false,
    },
  },
  data: function () {
    return {
      isEmpty: false,
      showPopup: false,
    };
  },
  computed: {
    chartOptions() {
      return this.constructChartOptions();
    },
    ifLoading() {
      return !Object.keys(this.histogram)?.length;
    },
  },
  methods: {
    chartFinished() {
      this.$emit(EMITTED_ACTIONS.FINISHED, true);
    },
    getDataFromHistogram() {
      let dates = [];
      let markLines = [];
      let positive = [];
      let negative = [];

      for (let i = this.histogram.length; i > 0; i--) {
        let magicIndex = this.histogram.length - i;
        if (
          i !== this.histogram.length &&
          i !== 1 &&
          magicIndex % this.markLineInterval === 0
        ) {
          markLines.push({
            xAxis: dayjs
              .utc(this.histogram[i - 1].key_as_string)
              .format(this.dateFormat),
          });
        }
      }

      let allZero = true;
      this.isEmpty = false;
      for (let i = 0; i < this.histogram.length; i++) {
        let theDay = dayjs.utc(this.histogram[i].key_as_string);
        dates.push(theDay.format(this.dateFormat));

        positive.push(this.histogram[i].positiveCount.doc_count);

        let neg = this.histogram[i].negativeCount.doc_count;
        if (neg > 0) {
          neg = neg * -1;
        }
        negative.push(neg);

        if (
          allZero &&
          (this.histogram[i].positiveCount.doc_count !== 0 || neg !== 0)
        ) {
          allZero = false;
        }
      }

      if (allZero && this.histogram?.length) {
        this.isEmpty = true;
      }

      return {
        dates: dates,
        markLines: markLines,
        positive: positive,
        negative: negative,
      };
    },
    constructChartOptions() {
      let theDates = [];
      let positiveValues = [];
      let negativeValues = [];
      let processedChartData = this.getDataFromHistogram();
      theDates = processedChartData.dates;
      positiveValues = processedChartData.positive;
      negativeValues = processedChartData.negative;

      let option = {
        tooltip: {
          textStyle: {
            color: "#FFF",
          },
          trigger: "item",
          axisPointer: {
            type: "shadow",
          },
          borderWidth: 0,
          backgroundColor: "rgba(3,3,3,0.7)",
          formatter: (params) => {
            let message = dayjs.utc(params.name).format("MMMM D, YYYY");

            let percentageOfType = Math.round(
              (Math.abs(params.value) /
                this.histogram[params.dataIndex].doc_count) *
                100
            );
            message += " - " + percentageOfType + "%";

            if (params.value < 0) {
              message += " negative data";
            } else {
              message += " positive data";
            }

            return message;
          },
        },
        toolbox: {
          show: false,
          feature: {
            mark: { show: false },
            dataView: { show: true, readOnly: false },
            magicType: { show: true, type: ["line", "bar"] },
            restore: { show: true },
            saveAsImage: { show: true },
          },
        },
        calculable: true,
        grid: {
          top: "15%",
          left: "5%",
          right: "5%",
          height: "50%",
        },
        series: [
          {
            name: "positive",
            type: "bar",
            stack: "main",
            barWidth: "75%",
            color: this.positiveBarColor,
            zlevel: 100,
            itemStyle: {
              normal: {
                label: {
                  show: false,
                },
              },
            },
            data: positiveValues,
          },
          {
            name: "negative",
            type: "bar",
            stack: "main",
            barWidth: "75%",
            color: this.negativeBarColor,
            zlevel: 100,
            itemStyle: {
              normal: {
                label: {
                  show: false,
                },
              },
            },
            data: negativeValues,
          },
        ],
      };

      let basicAxisOption = {
        axisTick: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        axisLabel: {
          show: false,
        },
      };

      let xAxis = JSON.parse(JSON.stringify(basicAxisOption));
      xAxis.type = "category";
      xAxis.data = theDates;
      xAxis.axisLabel = {
        show: true,
        color: "white",
        interval: (index) => {
          return (
            index === 0 ||
            index + 1 === this.histogram.length ||
            index === Math.floor(this.histogram.length / 2)
          );
        },
        formatter: (value) => {
          return dayjs.utc(value).format("MMM D");
        },
        inside: false,
        padding: [30, 0, 0, 0],
      };

      let yAxis = JSON.parse(JSON.stringify(basicAxisOption));
      yAxis.type = "value";

      option.yAxis = [yAxis];
      option.xAxis = [xAxis];

      return option;
    },
    reloadChart() {
      if (this.$refs.chartInstance) {
        this.$refs.chartInstance.resize();
      }
    },
  },
  watch: {
    refresh: {
      handler() {
        this.reloadChart();
      },
    },
  },
};
</script>

<style scoped>
.reload-icon {
  cursor: pointer;
}
.icon-container {
  display: flex;
  justify-content: flex-end;
  padding-right: 20px;
  top: -20px;
}
.icon-container.no-title {
  top: 0;
  margin-top: 10px;
}
.message {
  margin-top: 20px;
  margin-bottom: 40px;
  color: #ccc;
  font-size: 0.9em;
}
h3 {
  display: inline-block;
}
.description-icon {
  margin-left: 10px;
  top: -2px;
}
</style>
